/**
 * /admin/tickets — support ticket queue for admin_global.
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Ticket } from 'lucide-react';
import { watchAllTickets, watchAllTicketsByStatus } from '@/lib/firestore';
import type { Ticket as TicketDoc, TicketStatus } from '@/types/models';
import { Badge } from '@/components/ui/badge';
import { Loader } from '@/components/ui/loader';
import { EmptyState } from '@/components/ui/empty-state';
import { Table, TableHead, TableBody, TableRow, TableHeadCell, TableCell } from '@/components/ui/table';
import { useT } from '@/lib/i18n';
import { fmtRelative, cn } from '@/lib/utils';

const STATUSES: (TicketStatus | 'all')[] = ['all', 'open', 'in_progress', 'closed'];

function ticketBadgeVariant(status: TicketStatus) {
  if (status === 'open') return 'warning' as const;
  if (status === 'in_progress') return 'info' as const;
  return 'neutral' as const;
}

export default function AdminTickets() {
  const { t, locale } = useT();
  const navigate = useNavigate();

  const [tickets, setTickets] = useState<TicketDoc[] | null>(null);
  const [statusFilter, setStatusFilter] = useState<TicketStatus | 'all'>('open');

  useEffect(() => {
    if (statusFilter === 'all') {
      return watchAllTickets(setTickets);
    }
    return watchAllTicketsByStatus(statusFilter, setTickets);
  }, [statusFilter]);

  return (
    <div className="space-y-6">
      <header>
        <h1 className="font-display text-2xl text-ink-900 dark:text-white">{t('admin.tickets.title')}</h1>
        <p className="mt-1 text-sm text-ink-500">{t('admin.tickets.subtitle')}</p>
      </header>

      {/* Status filters */}
      <div className="flex flex-wrap gap-2">
        {STATUSES.map(s => (
          <button
            key={s}
            onClick={() => setStatusFilter(s)}
            className={cn(
              'rounded-full px-3 py-1 text-xs font-medium transition',
              statusFilter === s
                ? 'bg-brand-600 text-white dark:bg-primary'
                : 'border border-ink-200 text-ink-600 hover:bg-ink-50 dark:border-border dark:text-foreground/70 dark:hover:bg-secondary',
            )}
          >
            {t(`support.filter.${s}` as any)}
          </button>
        ))}
      </div>

      {tickets === null ? (
        <Loader fullscreen={false} />
      ) : tickets.length === 0 ? (
        <EmptyState
          icon={<Ticket className="h-5 w-5" />}
          title={t('admin.tickets.empty.title')}
          description={t('admin.tickets.empty.desc')}
        />
      ) : (
        <Table>
          <TableHead>
            <tr>
              <TableHeadCell>{t('support.col.subject')}</TableHeadCell>
              <TableHeadCell>{t('admin.tickets.col.org')}</TableHeadCell>
              <TableHeadCell>{t('support.col.category')}</TableHeadCell>
              <TableHeadCell>{t('support.col.status')}</TableHeadCell>
              <TableHeadCell>{t('support.col.messages')}</TableHeadCell>
              <TableHeadCell>{t('support.col.updated')}</TableHeadCell>
            </tr>
          </TableHead>
          <TableBody>
            {tickets.map(ticket => {
              const awaitingReply = ticket.lastMessageByRole === 'user' && ticket.status !== 'closed';
              return (
                <TableRow
                  key={ticket.id}
                  onClick={() => navigate(`/admin/tickets/${ticket.id}`)}
                  className="cursor-pointer hover:bg-slate-50 dark:hover:bg-secondary/40"
                >
                  <TableCell className="max-w-[260px]">
                    <div className="flex items-center gap-2">
                      {awaitingReply && (
                        <span className="h-2 w-2 shrink-0 rounded-full bg-amber-500" title={t('admin.tickets.unread')} />
                      )}
                      <p className="truncate font-medium text-ink-900 dark:text-foreground">{ticket.subject}</p>
                    </div>
                    {ticket.lastMessagePreview && (
                      <p className="mt-0.5 truncate text-xs text-ink-400 dark:text-muted-foreground">
                        {ticket.lastMessagePreview}
                      </p>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-ink-500 dark:text-muted-foreground max-w-[160px] truncate">
                    {ticket.orgName}
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-ink-500 dark:text-muted-foreground">
                      {t(`support.category.${ticket.category}` as any)}
                    </span>
                  </TableCell>
                  <TableCell>
                    <Badge variant={ticketBadgeVariant(ticket.status)}>
                      {t(`support.status.${ticket.status}` as any)}
                    </Badge>
                  </TableCell>
                  <TableCell>{ticket.messageCount}</TableCell>
                  <TableCell className="text-xs text-ink-400 dark:text-muted-foreground">
                    {fmtRelative(ticket.updatedAt, t as any, locale)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}
    </div>
  );
}
